import moment from "moment";
import { timesheetActionTypes } from "./timesheet-actions";
import { ADD_TIMESHEET_ROUTE } from "./timesheet-routes";

export const defaultState = {
	siteId: "",
	organisationTaskTypeId: "",
	equipment: [],
	captureEquipmentUsage: false,
	timesheet: {
		staffId: "",
		startDate: "",
		endDate: "",
		lunchTime: "",
		startTime: "",
		endTime: "",
		notes: "",
		toggle: false,
		startLatitude: null,
		startLongitude: null,
		endLatitude: null,
		endLongitude: null,
	},
	isValid: false,
	isEndDateTimeInvalid: false,
	isLoading: false,
	success: false,
	errorMessage: ""
};

export const timesheetFormReducer = (state = defaultState, action) => {
	switch (action.type) {
		case timesheetActionTypes.ADD_TIMESHEET_FORM_RESET: {
			if (action.payload) {
				return {
					...defaultState,
					...action.payload,
					timesheet: {
						...defaultState.timesheet,
						...action.payload.timesheet
					}
				};
			}

			return defaultState;
		}
		case timesheetActionTypes.ADD_TIMESHEET_FORM_VALUE_CHANGE: {
			return isValid({
				...state,
				...action.payload,
			});
		}
		case timesheetActionTypes.ADD_TIMESHEET_FORM_SUBMIT:
			return setLoading(state);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_SUBMIT_FAILED:
			return setError(state, action.payload);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_SUBMIT_SUCCESS:
			return setSuccess(state);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_START_TIMER:
			return setLoading(startTimer(state, action));
		case timesheetActionTypes.ADD_TIMESHEET_FORM_START_SUCCESS:
			return setSuccess(startTimerSuccess(state));
		case timesheetActionTypes.ADD_TIMESHEET_FORM_START_FAILED:
			return setError(state, action.payload);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_STOP_TIMER:
			return stopTimer(state, action);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_CONFIRM:
			return setLoading(state);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_CONFIRM_SUCCESS:
			// Clear active flag immediately (don't wait for a separate reset).
			// Stale toggle left the menu/top-bar showing "Active Timesheet" after stop+submit.
			return setSuccess(clearActiveTimesheetState(state));
		case timesheetActionTypes.ADD_TIMESHEET_FORM_CONFIRM_FAILED:
			return setError(state, action.payload);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_DISCARD:
			return setLoading(state);
		case timesheetActionTypes.ADD_TIMESHEET_FORM_DISCARD_SUCCESS:
			return setSuccess(clearActiveTimesheetState(state));
		case timesheetActionTypes.ADD_TIMESHEET_FORM_DISCARD_FAILED:
			return setError(state, action.payload);
		case timesheetActionTypes.UPDATE_TIMESHEET_FORM_SUBMIT:
			return setLoading(state);
		case timesheetActionTypes.ACTIVE_TIMESHEET_RECEIVED:
			return activeTimesheetReceived(state, action.payload);
		default:
			return state;
	}
};

const setLoading = (state) => {
	return {
		...state,
		isLoading: true,
		success: false,
		errorMessage: ""
	};
};

const setSuccess = (state) => {
	return {
		...state,
		isLoading: false,
		success: true,
		errorMessage: ""
	};
};

const setError = (state, payload) => {
	return {
		...state,
		isLoading: false,
		success: false,
		errorMessage: payload
	};
};

const isValid = (state) => {
	state.isValid =
		!!(state.siteId &&
		state.timesheet.staffId &&
		state.timesheet.startDate &&
		state.timesheet.endDate &&
		state.timesheet.startTime &&
		state.timesheet.endTime);
	state.isEndDateTimeInvalid =
		!!(state.timesheet.startDate &&
		state.timesheet.endDate &&
		state.timesheet.startTime &&
		state.timesheet.endTime &&
		moment(
			state.timesheet.endDate + state.timesheet.endTime,
			"YYYY-MM-DDHH:mm"
		).isBefore(
			moment(
				state.timesheet.startDate + state.timesheet.startTime,
				"YYYY-MM-DDHH:mm"
			)
		));
	return state;
};

const startTimer = (state, action) => {
	if (!state) {
		return defaultState;
	}
	
	const newState = {
		...state
	};

	newState.timesheet.staffId = action.payload.staffId;
	newState.timesheet.startLatitude = action.payload.latitude;
	newState.timesheet.startLongitude = action.payload.longitude;

	// If a start date/time has already been chosen (e.g. via manual entry on the form),
	// respect that value. Otherwise default to "now".
	if (!newState.timesheet.startDate) {
		newState.timesheet.startDate = moment().format("YYYY-MM-DD");
	}
	if (!newState.timesheet.startTime) {
		newState.timesheet.startTime = moment().format("HH:mm");
	}

	// Capture an exact start instant (UTC) so elapsed time doesn't "jump" by the current seconds.
	// Build from the (possibly manual) startDate/startTime so user-chosen values are honoured.
	if (!newState.timesheet.startDateTimeUTC) {
		newState.timesheet.startDateTimeUTC = moment(
			newState.timesheet.startDate + " " + newState.timesheet.startTime,
			"YYYY-MM-DD HH:mm"
		).toISOString();
	}

	return newState;
};

const startTimerSuccess = (state) => {
	if (!state) {
		return defaultState;
	}
	
	const newState = {
		...state
	};

	newState.timesheet.toggle = true;
	newState.timerMessage = `Timesheet started at ${moment(state.timesheet.startDate + " " + state.timesheet.startTime, "YYYY-MM-DD HH:mm").format("Do MMMM YYYY, h:mm a")}`;

	return newState;
};

const stopTimer = (state, action) => {
	if (!state) {
		return defaultState;
	}
	
	const newState = {
		...state
	};

	newState.timesheet.endLatitude = action.payload.latitude;
	newState.timesheet.endLongitude = action.payload.longitude;
	newState.timesheet.endDate = moment().format("YYYY-MM-DD");
	newState.timesheet.endTime = moment().format("HH:mm");

	return newState;
};

const clearActiveTimesheetState = (state) => {
	if (!state) {
		return defaultState;
	}
	return {
		...state,
		timerMessage: "",
		timesheet: {
			...state.timesheet,
			id: "",
			isActive: false,
			toggle: false,
			notes: "",
			endDate: "",
			endTime: "",
			endLatitude: null,
			endLongitude: null,
			startDateTimeUTC: undefined,
		},
	};
};

const activeTimesheetReceived = (state, payload) => {
	if (!state) {
		return defaultState;
	}
	
	if (!payload) {
		// Important: if API returns no active timesheet, clear any previously active timesheet
		// (prevents leaking a previous user's active timesheet into impersonation / org switches).
		return {
			...clearActiveTimesheetState(state),
			isLoading: false,
		};
	}

	return {
		...state,
		siteId: payload.siteId,
		organisationTaskTypeId: payload.organisationTaskTypeId,
		equipment: payload.equipment || [],
		timerMessage: `Timesheet started at ${moment.utc(
			payload.startDateTimeUTC).local().format("Do MMMM YYYY, h:mm a")}`,
		isLoading: false, // Reset loading state when restoring active timesheet
		timesheet: {
			...state.timesheet,
			id: payload.id,
			isActive: payload.isActive,
			staffId: payload.staffId,
			startLatitude: payload.startLatitude,
			startLongitude: payload.startLongitude,
			startDateTimeUTC: payload.startDateTimeUTC,
			startDate: moment.utc(payload.startDateTimeUTC).local().format("YYYY-MM-DD"),
			startTime: moment.utc(payload.startDateTimeUTC).local().format("HH:mm"),
			toggle: true,
			notes: payload.notes ?? payload.Notes ?? "",
		},
	};
};
